import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import _throttle from "lodash/throttle";
import { cn } from "helpers/style";
import { LinkProps } from "helpers/url";
import styles from "./BundleList.module.scss";
import BundleBadge from "./components/BundleBadge/BundleBadge";

type Props = {
  items?: Entity.Product.BundleList;
  wrapped?: boolean;
  className?: string;
  link?: LinkProps;
};

const BundleList: FC<Props> = ({ items, wrapped, className, link }: Props) => {
  const wrapper = useRef<HTMLDivElement>();
  const [visible, setVisible] = useState<number>(items.length);

  const onResize = useCallback(
    _throttle(() => {
      const wrapperItem = wrapper.current;

      if (!wrapperItem) return;

      let skip = false;

      const visibleCount = Array.from(wrapperItem.children).reduce(
        (acc: number, child: HTMLElement, index: number) => {
          skip = skip ? true : index > 0 && child.offsetLeft === 0;
          return skip ? acc : index + 1;
        },
        0
      );

      setVisible(visibleCount as number);
    }, 300),
    []
  );

  useEffect(() => {
    if (!wrapped) {
      onResize();
      window.addEventListener("resize", onResize);
    }

    return () => {
      if (!wrapped) {
        window.removeEventListener("resize", onResize);
      }
    };
  }, [onResize, wrapped]);

  const itemsLeft = items.length - visible;

  return (
    <div
      ref={wrapper}
      className={cn([
        styles.bundleList,
        wrapped && styles.bundleListWrapped,
        items.length === 1 && styles.bundleListWithoutPadding,
        className,
      ])}
    >
      {items.map((item, index) => {
        return (
          <BundleBadge
            key={item.id}
            item={item}
            showCounter={itemsLeft > 0 && index + 1 === visible && !wrapped}
            count={itemsLeft}
            productLink={link}
          />
        );
      })}
    </div>
  );
};

export default BundleList;
